import React, { useState, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { Stack, Pivot, PivotItem, List, IconButton, Icon, Panel, PanelType, ThemeProvider, makeStyles, useTheme } from '@fluentui/react';
import { FontSizes, FontWeights, DirectionalHint } from '@fluentui/react';

import { BRAND_NAVATAR } from 'src/constants';

import MainDecorator from 'src/components/base/MainDecorator';
import { getRedirectionUrl } from 'src/api';
import { runShortAction } from 'src/services/utils';
import { Category } from 'shared/utils/flowstep';
import { openHubspot } from '@v2/services/provider-url-openner';

import TeachingCoachmark from 'src/components/base/TeachingCoachmark';
import { useIdAsTarget } from 'shared/hooks';

import {coachmarkStore, userStore} from 'src/stores/index';
import { capitalizeString } from 'shared/utils/utils';
import { AccountType } from 'shared/types/flowstep';


const useCustomStyles = makeStyles(({ palette }) => ({
  sideMenu: {
    height: '100%',
  },
  listItemRoot: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',

    minHeight: 48,
    width: '100%',

    color: 'white',
    textDecoration: 'none',
    selectors: {
      ':hover': {
        backgroundColor: 'lightgrey',
      },
    }
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    margin: '0px 16px 0px 16px',
    minWidth: 0,

  },
  listItemTitleContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: 16,
    minWidth: 0,
  },
  listItemTitle: {
    fontSize: FontSizes.large,
    fontWeight: FontWeights.semilight,
  },
  listItemIcon: {
    fontSize: 24,
  },
  listItemDetail: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },

  stackSpacer: {
    flexGrow: 1,
  },
  helpItem: {
    marginRight: '32px',
  }
}))


const ComponentWithTeachingBubble = ({ theme, teaching, children }) => {
  const [id, target] = useIdAsTarget('with-bubble');

  return (
    <>
      <div id={id} style={{ height: 'inherit' }}>
        {children}
      </div>
      <TeachingCoachmark target={target} theme={theme} {...teaching} />
    </>
  );
}

const TextWithTeachingBubble = ({ text, ...props }) => (
  <ComponentWithTeachingBubble {...props}>
    <span>{text}</span>
  </ComponentWithTeachingBubble>
);

const IconWithTeachingBubble = ({ iconName, ...props }) => (
  <ComponentWithTeachingBubble {...props}>
    <Icon iconName={iconName} style={{ fontSize: 20 }} />
  </ComponentWithTeachingBubble>
);


export const Navigation = inject('officeStore', 'userStore')(observer(({ officeStore, userStore }) => {
  const theme = useTheme();
  const classNames = useCustomStyles();
  const history = useHistory();
  const location = useLocation();
  const [stackId, stackTarget] = useIdAsTarget('root');

  const { brand, license, accountType } = userStore;

  const pivotItems = [
    license.home.seeTab.can &&
    {
      to: `/home/${Category.Pull}`,
      onRender: (item, defaultRenderer) => <IconWithTeachingBubble
        teaching={{ id: 'home-icon' }}
        iconName={'home'} {...item} theme={theme}
      />,
      // icon: 'home'
    },
    license.flows.seeTab.can &&
    {
      to: '/home/flows',
      onRender: (item, defaultRenderer) => <TextWithTeachingBubble
        teaching={{ id: 'flows-icon' }}
        text={'Flows'} {...item} theme={theme}
      />,
    },
    license.scheduler.seeTab.can &&
    {
      title: 'Scheduler',
      to: '/home/scheduler',
    },
    userStore.accountType !== AccountType.HS &&  {
      to: '/home/context',
      onRender: (item, defaultRenderer) => <TextWithTeachingBubble
        teaching={{ id: 'context-icon' }}
        text={'Context'} {...item} theme={theme}
      />,
      helpLink: `https://www.xappex.com/docs/enabler4excel365/dealing-with-picklist-and-lookup-fields/`,
    },
  ].filter(Boolean);

  const [isOpenDrawer, setOpenDrawer] = useState(false);
  const selectedTab = pivotItems.find(v => location.pathname.startsWith(v.to)) || pivotItems[0];
  const { helpLink } = selectedTab;

  function handlePivotItemClick({ props }) {
    history.push(props.itemKey);
  }

  function handleOpenInSalesforce() {
    runShortAction(async () => {
      await officeStore.openInSalesforce();
      setOpenDrawer(false);
    });
  }

  function handleOpenInHubspot() {
    runShortAction(async () => {
      await openHubspot();
      setOpenDrawer(false);
    });
  }

  function handleOpenHelp() {
    window.open(getRedirectionUrl({ url: brand === BRAND_NAVATAR ? 'http://www.navatargroup.com/support' : 'https://www.xappex.com' }));
    setOpenDrawer(false);
  }

  function handleTakeTourClick() {
    setOpenDrawer(false);
    coachmarkStore.takeTourAgain();
  }

  const OpenIn = {
    [AccountType.SF]: {
      text: 'Open in Salesforce',
      onClick: handleOpenInSalesforce,
    },
    [AccountType.HS]: {
      text: 'Open in HubSpot',
      onClick: handleOpenInHubspot,
    }
  }

  const sideMenuitems = [
    { title: brand === BRAND_NAVATAR ? 'Open in Navatar' : OpenIn[accountType].text, iconName: 'xappex-share', onClick: OpenIn[accountType].onClick },
    license.scheduler.seeTab.can && { title: 'Manage Schedules', iconName: 'xappex-schedules', to: '/schedules' },
    { title: 'Settings', iconName: 'xappex-settings', to: '/settings' },
    userStore.settings?.debugMode && { title: 'Debug', iconName: 'Bug', to: '/debug' },
    license.canSeeLicenseInformation ? { title: 'License', detail: license.plan, iconName: 'xappex-license', to: '/license' } : null,
    { title: 'About', iconName: 'xappex-about', to: '/about' },
    { title: 'Help', iconName: 'xappex-help', onClick: handleOpenHelp },
    brand !== BRAND_NAVATAR
      ? { title: 'Accounts', detail: userStore.userName, iconName: 'xappex-source', to: '/accounts' }
      : { title: 'Log Out', detail: userStore.userName, iconName: 'xappex-signout', onClick: () => userStore.signOut() }, ,
  ].filter(Boolean);

  if (process.env.NODE_ENV === 'development') {
    sideMenuitems.push({
      title: 'Experiment',
      detail: 'Click is you know',
      iconName: 'xappex-settings',
      onClick: () => { history.push('/experiment') }
    },
    )
  }

  // const teaching = { id: 'navigation-pane', directionalHint: DirectionalHint.bottomCenter };
  const teaching = { id: 'navigation-pane', directionalHint: DirectionalHint.bottomAutoEdge };

  return (
    <ThemeProvider theme={theme.Navigation}>
      <Stack horizontal id={stackId}>
        <Stack.Item align="center">
          <IconButton iconProps={{ iconName: 'CollapseMenu' }} onClick={() => setOpenDrawer(true)} />
        </Stack.Item>

        <Pivot
          headersOnly
          selectedKey={selectedTab.to}
          onLinkClick={handlePivotItemClick}
          styles={{ link: { marginRight: '0px' }, linkIsSelected: { marginRight: '0px' } }}
        >
          {pivotItems.map(({ title, to, icon, onRender }) => <PivotItem
            headerText={title}
            itemKey={to}
            key={to}
            itemIcon={icon}
            onRenderItemLink={onRender}
            headerButtonProps={{ styles: { root: { verticalAlign: 'top' } } }}
          />)}
        </Pivot>

        <div className={classNames.stackSpacer} />

        {
          helpLink &&
          <Stack.Item align="center" className={classNames.helpItem}>
            <IconButton iconProps={{ iconName: 'Help' }} onClick={() => window.open(helpLink, '_blank')} />
          </Stack.Item>
        }
      </Stack>

      <TeachingCoachmark target={stackTarget} theme={theme} {...teaching} />

      <Panel
        isOpen={isOpenDrawer}
        type={PanelType.smallFixedNear}
        isLightDismiss={true}
        isFooterAtBottom={true}
        styles={{ scrollableContent: { display: 'flex', flexDirection: 'column' } }}
        onDismiss={() => setOpenDrawer(false)}
        onRenderBody={() => (
          <MainDecorator data-is-scrollable="true" isSidePanel={true}>
            <List className={classNames.sideMenu} items={sideMenuitems} onRenderCell={item => <ListItem {...item} />} />
          </MainDecorator>
        )}
        onRenderNavigation={() => null}
      />
    </ThemeProvider>
  )
}))

const ListItem = ({ title, detail, iconName, to = '#', onClick }) => {
  const classNames = useCustomStyles();
  return (
    <Link className={classNames.listItemRoot} to={to} onClick={onClick}>
      <div className={classNames.listItem}>
        <Icon iconName={iconName} className={classNames.listItemIcon} />
        <div className={classNames.listItemTitleContent}>
          <div className={classNames.listItemTitle}>{title}</div>
          {detail && <small className={classNames.listItemDetail}>{detail}</small>}
        </div>
      </div>
    </Link>
  )
}


export default Navigation;
